import { useState, useRef, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { TopNav } from '@/components/layout/TopNav';
import {
  mediaApi,
  queuesApi,
  postsApi,
  aiApi,
  socialAccountsApi,
  type MediaItem,
} from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { cn, formatBytes } from '@/lib/utils';
import {
  Upload,
  Trash2,
  Image as ImageIcon,
  Video as VideoIcon,
  Sparkles,
  Loader2,
  CheckCircle2,
} from 'lucide-react';

interface Draft {
  localId: string;
  media: MediaItem;
  caption: string;
  generating: boolean;
  status: 'pending' | 'uploading' | 'ready' | 'queued' | 'failed';
  error?: string;
}

export function BulkPosting() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<string>('');
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const { data: queuesData } = useQuery({
    queryKey: ['queues', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return queuesApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const { data: accountsData } = useQuery({
    queryKey: ['social-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return socialAccountsApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const queues = queuesData?.queues ?? [];
  const accounts = accountsData?.accounts ?? [];

  const handleFiles = async (files: File[]) => {
    if (!files.length || !currentWorkspace) return;
    setUploading(true);
    const token = await getToken();

    const newDrafts: Draft[] = files.map((f) => ({
      localId: `${Date.now()}-${f.name}-${Math.random().toString(36).slice(2, 7)}`,
      media: {
        id: '',
        workspace_id: currentWorkspace.id,
        r2_key: '',
        filename: f.name,
        mime_type: f.type,
        size_bytes: f.size,
        width: null,
        height: null,
        duration_seconds: null,
        source: 'upload',
        created_at: new Date().toISOString(),
      },
      caption: '',
      generating: false,
      status: 'uploading',
    }));
    setDrafts((prev) => [...prev, ...newDrafts]);

    await Promise.all(
      files.map(async (file, idx) => {
        const localId = newDrafts[idx].localId;
        try {
          // Direct upload — actually stores the bytes in R2 (the old multipart
          // upload-url/confirm flow created a row but never uploaded the file).
          const { media } = await mediaApi.upload(token!, currentWorkspace.id, file);
          setDrafts((prev) =>
            prev.map((d) => (d.localId === localId ? { ...d, media, status: 'ready' } : d))
          );
        } catch (err) {
          setDrafts((prev) =>
            prev.map((d) =>
              d.localId === localId
                ? { ...d, status: 'failed', error: err instanceof Error ? err.message : 'Upload failed' }
                : d
            )
          );
        }
      })
    );

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const generateCaption = async (localId: string) => {
    const draft = drafts.find((d) => d.localId === localId);
    if (!draft || !currentWorkspace || !draft.media.r2_key) return;
    setDrafts((prev) => prev.map((d) => (d.localId === localId ? { ...d, generating: true } : d)));
    try {
      const token = await getToken();
      const resp = await aiApi.generateCaption(token!, {
        workspaceId: currentWorkspace.id,
        mediaKey: draft.media.r2_key,
        // Give the model a platform to write for (mirrors the Composer).
        platform: accounts.find((a) => a.id === selectedAccounts[0])?.platform,
      });
      setDrafts((prev) =>
        prev.map((d) =>
          d.localId === localId ? { ...d, caption: resp.caption, generating: false } : d
        )
      );
    } catch (err) {
      setDrafts((prev) =>
        prev.map((d) =>
          d.localId === localId
            ? { ...d, generating: false, error: err instanceof Error ? err.message : 'Caption failed' }
            : d
        )
      );
    }
  };

  const addAllToQueue = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace || !selectedQueue) throw new Error('Pick a queue first');
      if (selectedAccounts.length === 0) throw new Error('Pick at least one social account');

      const token = await getToken();
      const ready = drafts.filter((d) => d.status === 'ready' && d.caption.trim());
      if (!ready.length) throw new Error('No drafts ready — add captions to your uploads');

      for (const draft of ready) {
        try {
          const resp = await postsApi.create(token!, {
            workspace_id: currentWorkspace.id,
            content: draft.caption,
            platforms: selectedAccounts,
            media_keys: [draft.media.r2_key],
            queue_id: selectedQueue,
          });
          // The post may be created but fail queue placement (paused queue, no
          // free slot) — that's a failure for this flow, not a success.
          if (resp.enqueued === false) {
            throw new Error(resp.enqueue_error ?? 'Could not place the post in the queue');
          }
          setDrafts((prev) =>
            prev.map((d) => (d.localId === draft.localId ? { ...d, status: 'queued' } : d))
          );
        } catch (err) {
          setDrafts((prev) =>
            prev.map((d) =>
              d.localId === draft.localId
                ? { ...d, status: 'failed', error: err instanceof Error ? err.message : 'Failed to enqueue' }
                : d
            )
          );
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      qc.invalidateQueries({ queryKey: ['queues', currentWorkspace?.id] });
    },
  });

  const readyCount = useMemo(
    () => drafts.filter((d) => d.status === 'ready' && d.caption.trim()).length,
    [drafts]
  );
  const queuedCount = useMemo(() => drafts.filter((d) => d.status === 'queued').length, [drafts]);

  // One click instead of N: caption every uploaded draft that doesn't have one
  // yet, a few at a time so the AI route isn't hammered.
  const [generatingAll, setGeneratingAll] = useState(false);
  const captionlessCount = useMemo(
    () => drafts.filter((d) => d.status === 'ready' && !d.caption.trim()).length,
    [drafts]
  );
  const generateAllCaptions = async () => {
    const targets = drafts.filter((d) => d.status === 'ready' && !d.caption.trim());
    if (!targets.length) return;
    setGeneratingAll(true);
    try {
      const CONCURRENCY = 3;
      for (let i = 0; i < targets.length; i += CONCURRENCY) {
        await Promise.allSettled(
          targets.slice(i, i + CONCURRENCY).map((d) => generateCaption(d.localId))
        );
      }
    } finally {
      setGeneratingAll(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) handleFiles(files);
  };

  if (!currentWorkspace) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Select a workspace to start bulk posting.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      <TopNav
        title="Bulk Posting"
        description="Upload many assets, caption them, and slot them into a queue."
        action={{
          label: uploading ? 'Uploading...' : 'Upload',
          onClick: () => fileInputRef.current?.click(),
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files ?? []))}
      />

      <div className="p-6 space-y-5 flex-1 overflow-auto mx-auto w-full max-w-[1400px]">
        {/* Queue + Accounts row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card ring-1 ring-hairline rounded-l shadow-kumo-m p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Destination queue
            </p>
            {queues.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No queues yet. Create one on the <span className="text-accent">Queues</span> page first.
              </p>
            ) : (
              <select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                className="w-full bg-input border border-border rounded-m px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">Choose a queue…</option>
                {queues.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name} ({q.slots?.length ?? 0} slots, {q.timezone})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="bg-card ring-1 ring-hairline rounded-l shadow-kumo-m p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Publish to accounts
            </p>
            {accounts.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Connect a social account in Settings → Social Accounts.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {accounts.map((a) => {
                  const active = selectedAccounts.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      onClick={() =>
                        setSelectedAccounts((prev) =>
                          active ? prev.filter((id) => id !== a.id) : [...prev, a.id]
                        )
                      }
                      className={cn(
                        'flex items-center gap-1.5 px-2.5 py-1 rounded-s text-xs ring-1 transition-colors',
                        active
                          ? 'bg-accent/20 text-foreground ring-accent/30'
                          : 'text-muted-foreground ring-hairline hover:bg-tertiary/40'
                      )}
                    >
                      <PlatformIcon platform={a.platform} size="xs" labelled />
                      {a.account_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'border-2 border-dashed rounded-l p-8 text-center cursor-pointer transition-colors',
            dragActive ? 'border-accent bg-accent/5' : 'border-hairline hover:border-accent/50'
          )}
        >
          <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">
            Drag and drop images and videos here
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Each file becomes a draft post you can caption and add to the queue.
          </p>
        </div>

        {/* Drafts grid */}
        {drafts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {drafts.map((d) => (
              <DraftCard
                key={d.localId}
                draft={d}
                onCaptionChange={(c) =>
                  setDrafts((prev) =>
                    prev.map((x) => (x.localId === d.localId ? { ...x, caption: c } : x))
                  )
                }
                onGenerate={() => generateCaption(d.localId)}
                onRemove={() =>
                  setDrafts((prev) => prev.filter((x) => x.localId !== d.localId))
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Action footer */}
      {drafts.length > 0 && (
        <div className="sticky bottom-0 border-t border-hairline bg-canvas/80 backdrop-blur-md px-6 py-3 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {readyCount} ready · {queuedCount} added to queue
            {addAllToQueue.error && (
              <span className="ml-3 text-danger">{(addAllToQueue.error as Error).message}</span>
            )}
          </div>
          {captionlessCount > 0 && (
            <button
              onClick={generateAllCaptions}
              disabled={generatingAll}
              className="flex items-center gap-2 px-3 py-2 text-xs rounded-m bg-accent/10 text-accent ring-1 ring-accent/30 hover:bg-accent/20 disabled:opacity-50 transition-colors"
            >
              {generatingAll ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Generate {captionlessCount} caption{captionlessCount === 1 ? '' : 's'}
            </button>
          )}
          <button
            onClick={() => addAllToQueue.mutate()}
            disabled={
              addAllToQueue.isPending ||
              !selectedQueue ||
              readyCount === 0 ||
              selectedAccounts.length === 0
            }
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-m text-sm font-medium hover:bg-accent/90 disabled:opacity-50 shadow-kumo-m transition-all"
          >
            {addAllToQueue.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add {readyCount} {readyCount === 1 ? 'post' : 'posts'} to queue
          </button>
        </div>
      )}
    </div>
  );
}

function DraftCard({
  draft,
  onCaptionChange,
  onGenerate,
  onRemove,
}: {
  draft: Draft;
  onCaptionChange: (c: string) => void;
  onGenerate: () => void;
  onRemove: () => void;
}) {
  const isImage = draft.media.mime_type?.startsWith('image/');
  const isQueued = draft.status === 'queued';
  const isFailed = draft.status === 'failed';

  return (
    <div
      className={cn(
        'flex flex-col bg-card ring-1 ring-hairline rounded-l overflow-hidden shadow-kumo-m',
        isQueued && 'ring-success/40'
      )}
    >
      <div className="relative aspect-video bg-muted flex items-center justify-center overflow-hidden">
        {draft.status === 'uploading' || !draft.media.r2_key ? (
          <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
        ) : isImage ? (
          <img
            src={mediaApi.serveUrl(draft.media.r2_key)}
            alt={draft.media.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <video src={mediaApi.serveUrl(draft.media.r2_key)} muted preload="metadata" className="w-full h-full object-cover" />
        )}
        {isQueued && (
          <div className="absolute top-2 right-2 bg-success text-success-foreground text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Queued
          </div>
        )}
      </div>
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-foreground truncate">{draft.media.filename}</p>
            {draft.media.size_bytes && (
              <p className="text-[11px] text-muted-foreground">{formatBytes(draft.media.size_bytes)}</p>
            )}
          </div>
          <button
            onClick={onRemove}
            disabled={isQueued}
            className="text-muted-foreground hover:text-danger p-1 rounded-s hover:bg-danger/10 disabled:opacity-30 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>

        <textarea
          value={draft.caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Caption..."
          rows={3}
          disabled={isQueued}
          className="w-full bg-input border border-border rounded-s px-2.5 py-2 text-xs text-foreground placeholder:text-muted-foreground outline-none resize-none focus:ring-1 focus:ring-accent disabled:opacity-60"
        />

        <div className="flex items-center justify-between">
          <button
            onClick={onGenerate}
            disabled={draft.generating || draft.status !== 'ready'}
            className="flex items-center gap-1.5 px-2 py-1 text-[11px] rounded-s bg-accent/15 text-accent ring-1 ring-accent/30 hover:bg-accent/25 disabled:opacity-50 transition-colors"
          >
            {draft.generating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            Generate Caption
          </button>
          {isFailed && draft.error && (
            <span className="text-[10px] text-danger truncate ml-2" title={draft.error}>
              {draft.error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
